import React, { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import Modal from './Modal'
import { useLanguage } from '../lib/LanguageContext'

// ── Cancel confirmation modal ───────────────────────────────────────
// Captures a structured cancellation reason (matching the backend
// CancellationReason::CATEGORIES whitelist) plus optional free-text
// details. The category is what Phase 11 analytics will aggregate on,
// so we force the reception to pick one rather than leaving it empty.

// Common reasons — the `by` maps each to WHO cancelled so the diary can show patient / reception /
// us / system at a glance (Paul 2026-07-03). Free text goes in the notes below when 'Other'.
const CATEGORIES = [
  { value: 'patient_request',     label: 'Patient requested / can\u2019t make it', by: 'patient' },
  { value: 'not_needed',          label: 'Patient no longer needs treatment',      by: 'patient' },
  { value: 'rescheduling',        label: 'Rescheduling to another time',            by: 'reception' },
  { value: 'diary_error',         label: 'Double-booking / diary error',            by: 'reception' },
  { value: 'dentist_unavailable', label: 'Dentist unavailable / on leave',          by: 'practice' },
  { value: 'practice_closed',     label: 'Practice closed / emergency',             by: 'practice' },
  { value: 'no_response',         label: 'No response to confirmation',             by: 'reception' },
  { value: 'other',               label: 'Other (add a note below)',                by: 'reception' },
]

// 📉 WHY ISN'T THE PATIENT COMING? (lossreasons1014, Paul 2026-10-14). The mechanism above says who/how; this says WHY.
// Asked (required) only for the patient-side mechanisms. Keys = PatientLossReason::CATEGORIES + "not_said". Also used
// by the diary's Delete dialog.
export const LOSS_PATIENT_SIDE = ['patient_request', 'not_needed', 'no_response', 'other']
export const LOSS_CAUSES = [
  { value: 'cost_medical_aid',   label: 'Cost / medical aid' },
  { value: 'time_work',          label: 'Time / work' },
  { value: 'went_elsewhere',     label: 'Went elsewhere' },
  { value: 'fear_anxiety',       label: 'Fear / anxiety' },
  { value: 'feeling_better',     label: 'Feeling better / not needed' },
  { value: 'forgot_no_response', label: 'Forgot / no response' },
  { value: 'travel_distance',    label: 'Travel / distance / moved' },
  { value: 'illness',            label: 'Illness' },
  { value: 'unhappy_with_us',    label: 'Unhappy with us' },
  { value: 'availability_wait',  label: 'Dentist availability / waiting time' },
  { value: 'other',              label: 'Other' },
  { value: 'not_said',           label: 'Patient didn\u2019t say' },
]

export default function CancelAppointmentModal({ appointment, open, onClose }) {
  const { t } = useLanguage()
  const [category, setCategory] = useState('')
  const [details, setDetails] = useState('')
  const [cause, setCause] = useState('')
  const [patientWords, setPatientWords] = useState('')
  // Send the patient a WhatsApp cancellation message? (Paul 2026-07-04) — reception picks Yes/No per
  // cancel. Default Yes (a genuine cancellation confirms to the patient); No = remove it quietly so the
  // slot can be rebooked without the patient knowing (e.g. an unconfirmed no-show risk).
  const [notify, setNotify] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  // 🚨 A CONFIRMED patient cancelled as "No response to confirmation" is a contradiction (conv 100342:
  // a pain patient who TAPPED CONFIRM was dropped 3h before her slot). Force an explicit override.
  const [overrideConfirmed, setOverrideConfirmed] = useState(false)

  // Reset form whenever the modal (re)opens so a stale half-filled
  // state from a previous cancel doesn't leak into the next one.
  useEffect(() => {
    if (open) {
      setCategory('')
      setDetails('')
      setCause('')
      setPatientWords('')
      setNotify(true)
      setSubmitting(false)
      setOverrideConfirmed(false)
    }
  }, [open])

  // Picking "Rescheduling" implies a silent move (the new-time message goes out on rebook) — default the
  // toggle to No; reception can still flip it back.
  useEffect(() => { if (category === 'rescheduling') setNotify(false) }, [category])
  // Any reason change re-arms the confirmed-override guard so the acknowledgement can't leak across reasons.
  useEffect(() => { setOverrideConfirmed(false) }, [category])

  if (!appointment) return null

  // A confirmed appointment being cancelled as "no response" contradicts the patient's own Confirm tap.
  const confirmedNoResponse = appointment.status === 'confirmed' && category === 'no_response'
  const askCause = LOSS_PATIENT_SIDE.includes(category)

  const handleConfirm = () => {
    if (!category) {
      toast.error(t('cancel_pick_reason'))
      return
    }
    if (askCause && !cause) {
      toast.error(t('cancel_pick_cause'))
      return
    }
    if (askCause && cause === 'other' && !patientWords.trim()) {
      toast.error(t('cancel_words_required'))
      return
    }
    if (confirmedNoResponse && !overrideConfirmed) {
      toast.error('This patient CONFIRMED this appointment — tick the box to override, or choose a reason that fits.')
      return
    }
    setSubmitting(true)
    router.patch(
      `/appointments/${appointment.id}/cancel`,
      { cancellation: { category, details, cause: askCause ? cause : '', patient_words: askCause ? patientWords : '', rescheduling: category === 'rescheduling', send_cancellation: notify, override_confirmed: overrideConfirmed, cancelled_by: (CATEGORIES.find((c) => c.value === category)?.by || 'reception') } },
      {
        preserveScroll: true,
        preserveState: false,
        onSuccess: () => {
          toast.success(t('cancel_success'))
          onClose?.()
        },
        onError: () => toast.error(t('cancel_error')),
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('cancel_title')}
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {t('cancel_keep')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg transition-colors"
          >
            {submitting ? t('cancel_submitting') : t('cancel_confirm')}
          </button>
        </>
      }
    >
      <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-100 mb-5">
        <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-red-700">
          {t('cancel_warning')}{' '}
          <span className="font-semibold">{appointment.patient_name}</span>. {t('cancel_irreversible')}
        </div>
      </div>

      <label className="block mb-4">
        <span className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
          {t('cancel_reason_label')}
        </span>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-taupe/25 focus:border-brand-taupe"
        >
          <option value="">{t('cancel_reason_placeholder')}</option>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </label>

      {askCause && (
        <>
          <label className="block mb-4">
            <span className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
              {t('cancel_cause_label')}
            </span>
            <select
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-taupe/25 focus:border-brand-taupe"
            >
              <option value="">{t('cancel_cause_placeholder')}</option>
              {LOSS_CAUSES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className="block mb-4">
            <span className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
              {t('cancel_words_label')}{' '}
              <span className="text-gray-400 font-normal">{cause === 'other' ? t('cancel_words_required_tag') : t('cancel_notes_optional')}</span>
            </span>
            <textarea
              rows={2}
              value={patientWords}
              onChange={(e) => setPatientWords(e.target.value)}
              placeholder={t('cancel_words_placeholder')}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-taupe/25 focus:border-brand-taupe resize-none"
            />
          </label>
        </>
      )}

      {/* 🚨 Guard: a CONFIRMED appointment cancelled as "No response to confirmation" contradicts the
          patient's own Confirm tap (conv 100342). Warn and require an explicit override tick. */}
      {confirmedNoResponse && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 mb-4">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <span className="font-semibold">{appointment.patient_name} already CONFIRMED this appointment.</span>{' '}
            Cancelling it as “No response to confirmation” contradicts that. Pick the reason that really applies —
            or tick below to override.
            <label className="mt-2 flex items-center gap-2 font-medium text-amber-900">
              <input
                type="checkbox"
                checked={overrideConfirmed}
                onChange={(e) => setOverrideConfirmed(e.target.checked)}
                className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
              />
              Cancel anyway — I understand it was confirmed.
            </label>
          </div>
        </div>
      )}

      <label className="block">
        <span className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
          {t('cancel_notes_label')} <span className="text-gray-400 font-normal">{t('cancel_notes_optional')}</span>
        </span>
        <textarea
          rows={3}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder={t('cancel_notes_placeholder')}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-taupe/25 focus:border-brand-taupe resize-none"
        />
      </label>

      {/* Send the patient a cancellation message? (Paul 2026-07-04) — explicit per-cancel choice. "No"
          frees the slot to rebook WITHOUT the patient knowing (e.g. an unconfirmed no-show risk). */}
      <div className="mt-5">
        <span className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1.5">Send the patient a WhatsApp cancellation message?</span>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setNotify(true)}
            className={`rounded-lg border px-3 py-2 text-sm font-medium text-left transition-colors ${notify ? 'border-emerald-400 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            ✅ Yes — tell the patient
            <span className="block text-[11px] font-normal opacity-80">They get a "cancelled" WhatsApp (names the date + time).</span>
          </button>
          <button type="button" onClick={() => setNotify(false)}
            className={`rounded-lg border px-3 py-2 text-sm font-medium text-left transition-colors ${!notify ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            🤫 No — remove it quietly
            <span className="block text-[11px] font-normal opacity-80">Frees the slot to rebook; the patient is NOT told.</span>
          </button>
        </div>
      </div>
    </Modal>
  )
}
